import classNames from 'classnames';
import { memoize, sortBy } from 'lodash-es';
import moment from 'moment-timezone';
import React, { PureComponent, ReactNode } from 'react';

import { Motion } from '@sbu/components.motion';
import { Divider } from '@sumologic/yama-ds-components';

import { CountryFlag } from 'shared/components/elements/CountryFlag';
import { ContextActionContainer } from 'shared/components/internal/ContextAction';
import {
  LabeledPropertyType,
  LocationLabel,
  parseRecord,
  RecordType,
  spliceProperty,
} from 'shared/components/internal/Records';

import css from './NetworkRecord.module.scss';

interface ReqResType {
  [key: string]: any;
}

interface NetworkRecordProps {
  record: RecordType;
  showEmpty: boolean;
}

export class NetworkRecord extends PureComponent<NetworkRecordProps> {
  parseRequestProps = memoize((origRequest: ReqResType, showEmpty: boolean) => {
    // Get headers
    const request = { ...origRequest };
    let headers: LabeledPropertyType[] = [];
    if (request.headers) {
      headers = sortBy(
        parseRecord(request.headers, showEmpty),
        ({ label }) => label
      );
      delete request.headers;
    }

    // Get entropy
    let entropy: LabeledPropertyType[] = [];
    if (request.entropy) {
      entropy = sortBy(
        parseRecord(request.entropy, showEmpty),
        ({ label }) => label
      );
      delete request.entropy;
    }

    // Get user agent
    let userAgent: LabeledPropertyType[] = [];
    if (request.ua) {
      userAgent = sortBy(
        parseRecord(request.ua, showEmpty),
        ({ label }) => label
      );
      delete request.ua;
    }

    const properties = sortBy(
      parseRecord(request, showEmpty),
      ({ label }) => label
    );

    return { entropy, headers, properties, userAgent };
  });

  parseResponseProps = memoize(
    (origResponse: ReqResType, showEmpty: boolean) => {
      const response = { ...origResponse };
      let headers: LabeledPropertyType[] = [];
      if (response.headers) {
        headers = sortBy(
          parseRecord(response.headers, showEmpty),
          ({ label }) => label
        );
        delete response.headers;
      }
      const properties = sortBy(
        parseRecord(response, showEmpty),
        ({ label }) => label
      );

      return { headers, properties };
    }
  );

  renderHeader(label: string, value?: ReactNode, subheader?: ReactNode) {
    return (
      <div className={css.sectionHeader}>
        <div className={css.headerContent}>
          {label}
          {!!value && <span>:&nbsp;{this.renderContextAction(value)}</span>}
        </div>
        {!!subheader && <div className={css.subheader}>{subheader}</div>}
      </div>
    );
  }

  renderContextAction(value: ReactNode) {
    const { record } = this.props;

    return (
      <ContextActionContainer
        contextData={record}
        eventTimestamp={moment.unix(record.timestamp / 1000).toDate()}
      >
        {value === undefined ? (
          <span className={css.emptyResult}>None</span>
        ) : (
          value
        )}
      </ContextActionContainer>
    );
  }

  renderProperties(properties: LabeledPropertyType[], large: boolean = false) {
    return (
      <div className={classNames(css.properties, { [css.large]: large })}>
        {properties.map(({ name, label, value }) => (
          <div className={css.property} key={`property-${name}`}>
            <div className={css.propertyLabel}>{label}:</div>
            <div className={css.propertyValue}>
              {this.renderContextAction(value)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderAddress(type: 'source' | 'destination') {
    const { record, showEmpty } = this.props;
    const data = record[type === 'source' ? 'src_ip' : 'dst_ip'];
    const origProps = sortBy(
      parseRecord(data, showEmpty),
      ({ label }) => label
    );
    const { matches, properties } = spliceProperty(origProps, [
      'address',
      'city',
      'region',
      'country_name',
      'latitude',
      'longitude',
    ]);
    const countryCode = data.country_code;
    const [address, city, region, countryName, latitude, longitude] = matches;

    return (
      <div
        className={classNames({
          [css.secondColumn]: type === 'destination',
        })}
      >
        {this.renderHeader(
          type,
          address && address.value,
          <LocationLabel
            data={{ city, region, countryName, latitude, longitude }}
          />
        )}
        {this.renderProperties(properties, true)}
        {!!countryCode && (
          <div className={css.flag}>
            <CountryFlag countryCode={countryCode} size='xlarge' />
          </div>
        )}
      </div>
    );
  }

  renderSubdivider() {
    return <Divider />;
  }

  renderHeaders(headers: LabeledPropertyType[]) {
    return (
      <div>
        {headers.map((header) => (
          <pre className={css.headersValue} key={`header-${header.name}`}>
            {
              <span className={css.headerLabel}>
                {header.label}
                {': '}
              </span>
            }
            {this.renderContextAction(header.value)}
          </pre>
        ))}
      </div>
    );
  }

  renderRequest() {
    const { record, showEmpty } = this.props;
    const { entropy, headers, properties, userAgent } = this.parseRequestProps(
      record.request,
      showEmpty
    );
    const sideVisible = !!(
      headers.length ||
      entropy.length ||
      userAgent.length
    );

    return (
      <div>
        {this.renderHeader('Request')}
        <div className={css.requestResponse}>
          {sideVisible && (
            <div className={css.requestResponseSide}>
              {!!headers.length && (
                <div className={css.requestResponseCell}>
                  <div className={css.subContent}>
                    <div className={css.subHeader}>Headers</div>
                    {this.renderHeaders(headers)}
                  </div>
                </div>
              )}
              {!!entropy.length && (
                <div className={css.requestResponseCell}>
                  <div className={css.subContent}>
                    <Divider sx={{ mb: 2.5 }} />
                    <div className={css.subHeader}>Entropy</div>
                    {this.renderProperties(entropy)}
                  </div>
                </div>
              )}
              {!!userAgent.length && (
                <div className={css.requestResponseCell}>
                  <div className={css.subContent}>
                    <Divider sx={{ mb: 2.5 }} />
                    <div className={css.subHeader}>User Agent</div>
                    {this.renderProperties(userAgent)}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className={css.requestResponseBody}>
            {this.renderSubdivider()}
            <div className={css.content}>
              {this.renderProperties(properties, true)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderResponse() {
    const { record, showEmpty } = this.props;
    const { headers, properties } = this.parseResponseProps(
      record.response,
      showEmpty
    );

    return (
      <div>
        {this.renderHeader('Response')}
        <div className={css.requestResponse}>
          {!!headers.length && (
            <div className={css.requestResponseSide}>
              <div className={css.requestResponseCell}>
                <div className={css.subContent}>
                  <div className={css.subHeader}>Headers</div>
                  {this.renderHeaders(headers)}
                </div>
              </div>
            </div>
          )}
          <div className={css.requestResponseBody}>
            {this.renderSubdivider()}
            <div className={css.content}>
              {this.renderProperties(properties, true)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderOther() {
    const record: RecordType = { ...this.props.record };
    ['request', 'response', 'src_ip', 'dst_ip'].forEach(
      (name) => delete record[name]
    );
    const properties = sortBy(
      parseRecord(record, this.props.showEmpty),
      ({ label }) => label
    );

    return (
      <div>
        {this.renderHeader('Other')}
        {this.renderProperties(properties, true)}
      </div>
    );
  }

  render() {
    const { request, response } = this.props.record;

    return (
      <Motion>
        <div className={css.address}>
          <div className={css.source}>
            <Motion type='item'>{this.renderAddress('source')}</Motion>
          </div>
          <div className={css.destination}>
            <Motion type='item'>
              <Divider
                orientation='vertical'
                sx={{ left: 0, position: 'absolute', top: 0 }}
              />
              {this.renderAddress('destination')}
            </Motion>
          </div>
        </div>
        {!!request && (
          <Motion type='item'>
            <Divider sx={{ mb: 2.5 }} />
            {this.renderRequest()}
          </Motion>
        )}
        {!!response && (
          <Motion type='item'>
            <Divider sx={{ mb: 2.5 }} />
            {this.renderResponse()}
          </Motion>
        )}
        <Motion type='item'>
          <Divider sx={{ mb: 2.5 }} />
          {this.renderOther()}
        </Motion>
      </Motion>
    );
  }
}
